import { FunctionComponent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Icon, LoadingBlock, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { classNames } from "@/utils/classNames";

import {
	useCreateDynamicsFieldMappingMutation,
	useDeleteDynamicsFieldMappingMutation,
	useGetDynamicsAccountFieldsQuery,
	useGetDynamicsContactFieldsQuery,
	useGetDynamicsFieldMappingsQuery,
	useGetDynamicsLeadFieldsQuery,
	useGetDynamicsOpportunityFieldsQuery,
	useGetDynamicsSourceFieldsQuery,
	useTestDynamicsAiFieldMappingMutation,
	useUpdateDynamicsFieldMappingMutation,
} from "@/pages/Private/redux/dynamics/dynamics.api";
import { useLazyGetContactsQuery } from "@/pages/Private/redux/contact/contact.api";
import { useGetDynamicsCampaignResponseFieldsQuery } from "@/pages/Private/redux/dynamics/dynamicsCampaignResponse.api";
import {
	useGetDynamicsCampaignFieldsQuery,
	useGetDynamicsMarketingListFieldsQuery,
} from "@/pages/Private/redux/dynamics/dynamicsMarketingRecords.api";

import {
	FieldMappingAiConfig,
	FieldMappingSourceType,
	MsdynamicsEntityType,
	MsdynamicsField,
	MsdynamicsFieldMapping,
} from "../schema/dynamics";
import {
	DynamicsMappingRow,
	DynamicsNewMappingRow,
	NewDynamicsMapping,
} from "./DynamicsMappingRow";
import { DynamicsMappingPreviewModal } from "./DynamicsMappingPreviewModal";
import { DynamicsRestoreDefaultsButton } from "./DynamicsRestoreDefaultsButton";
import {
	collectUsedSourceFields,
	collectUsedTargetFields,
	DYNAMICS_TAB_ENTITY,
	DynamicsMappingTab,
	sourceFieldsForTab,
	targetFieldsForTab,
} from "./dynamicsFieldMapping.utils";
import {
	AiConfigModal,
	buildAiConfigLabels,
	buildAiProspectOptions,
	FieldMappingFooter,
} from "./fieldMapping";

interface DynamicsFieldMappingModalProps {
	isOpen: boolean;
	onClose: () => void;
}

interface DynamicsAiConfigSelection {
	id?: number;
	tempId?: string;
	label: string;
	targetField: MsdynamicsField;
	allFields: MsdynamicsField[];
	entityType: MsdynamicsEntityType;
	aiConfig?: FieldMappingAiConfig;
}

export const DynamicsFieldMappingModal: FunctionComponent<DynamicsFieldMappingModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>): string =>
		t(`company.msdynamics.fieldMapping.${key}`, options as never) as unknown as string;

	const [currentTab, setCurrentTab] = useState<DynamicsMappingTab>(DynamicsMappingTab.CONTACTS);
	const [newMappings, setNewMappings] = useState<NewDynamicsMapping[]>([]);
	const [savingTempId, setSavingTempId] = useState<string | null>(null);
	const [aiConfigTarget, setAiConfigTarget] = useState<DynamicsAiConfigSelection | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const aiProspectSearchRequestRef = useRef<{ abort?: () => void } | null>(null);

	const entityType = DYNAMICS_TAB_ENTITY[currentTab];

	const { data: contactMappings, isLoading: isLoadingContacts } = useGetDynamicsFieldMappingsQuery({
		entityType: MsdynamicsEntityType.CONTACT,
	});
	const { data: accountMappings, isLoading: isLoadingAccounts } = useGetDynamicsFieldMappingsQuery({
		entityType: MsdynamicsEntityType.ACCOUNT,
	});
	const { data: leadMappings, isLoading: isLoadingLeads } = useGetDynamicsFieldMappingsQuery({
		entityType: MsdynamicsEntityType.LEAD,
	});
	const { data: opportunityMappings, isLoading: isLoadingOpportunities } =
		useGetDynamicsFieldMappingsQuery({ entityType: MsdynamicsEntityType.OPPORTUNITY });
	const { data: responseMappings, isLoading: isLoadingResponses } =
		useGetDynamicsFieldMappingsQuery({ entityType: MsdynamicsEntityType.CAMPAIGN_RESPONSE });
	const { data: campaignMappings, isLoading: isLoadingCampaigns } =
		useGetDynamicsFieldMappingsQuery({ entityType: MsdynamicsEntityType.CAMPAIGN });
	const { data: marketingListMappings, isLoading: isLoadingMarketingLists } =
		useGetDynamicsFieldMappingsQuery({ entityType: MsdynamicsEntityType.MARKETING_LIST });

	const { data: contactFields } = useGetDynamicsContactFieldsQuery();
	const { data: accountFields } = useGetDynamicsAccountFieldsQuery();
	const { data: leadFields } = useGetDynamicsLeadFieldsQuery();
	const { data: opportunityFields } = useGetDynamicsOpportunityFieldsQuery();
	const { data: responseFields } = useGetDynamicsCampaignResponseFieldsQuery();
	const { data: campaignFields } = useGetDynamicsCampaignFieldsQuery();
	const { data: marketingListFields } = useGetDynamicsMarketingListFieldsQuery();
	const { data: sourceFields } = useGetDynamicsSourceFieldsQuery();

	const [createMapping] = useCreateDynamicsFieldMappingMutation();
	const [updateMapping] = useUpdateDynamicsFieldMappingMutation();
	const [deleteMapping] = useDeleteDynamicsFieldMappingMutation();
	const [testAiMapping] = useTestDynamicsAiFieldMappingMutation();
	const [searchProspects] = useLazyGetContactsQuery();

	const isLoading =
		isLoadingContacts ||
		isLoadingAccounts ||
		isLoadingLeads ||
		isLoadingOpportunities ||
		isLoadingResponses ||
		isLoadingCampaigns ||
		isLoadingMarketingLists;

	const mappingsByTab: Record<DynamicsMappingTab, MsdynamicsFieldMapping[] | undefined> = {
		[DynamicsMappingTab.CONTACTS]: contactMappings,
		[DynamicsMappingTab.ACCOUNTS]: accountMappings,
		[DynamicsMappingTab.LEADS]: leadMappings,
		[DynamicsMappingTab.OPPORTUNITIES]: opportunityMappings,
		[DynamicsMappingTab.CAMPAIGN_RESPONSES]: responseMappings,
		[DynamicsMappingTab.CAMPAIGNS]: campaignMappings,
		[DynamicsMappingTab.MARKETING_LISTS]: marketingListMappings,
	};

	const fieldsByTab: Record<DynamicsMappingTab, MsdynamicsField[]> = {
		[DynamicsMappingTab.CONTACTS]: contactFields?.fields ?? [],
		[DynamicsMappingTab.ACCOUNTS]: accountFields?.fields ?? [],
		[DynamicsMappingTab.LEADS]: leadFields?.fields ?? [],
		[DynamicsMappingTab.OPPORTUNITIES]: opportunityFields?.fields ?? [],
		[DynamicsMappingTab.CAMPAIGN_RESPONSES]: responseFields?.fields ?? [],
		[DynamicsMappingTab.CAMPAIGNS]: campaignFields?.fields ?? [],
		[DynamicsMappingTab.MARKETING_LISTS]: marketingListFields?.fields ?? [],
	};

	const currentTabNewMappings = useMemo(
		() => newMappings.filter((mapping) => mapping.entityType === entityType),
		[newMappings, entityType]
	);

	const usedSourceFields = useMemo(
		() => collectUsedSourceFields(mappingsByTab[currentTab], currentTabNewMappings),
		[mappingsByTab, currentTab, currentTabNewMappings]
	);

	const usedTargetFieldsByType = useMemo(
		() => collectUsedTargetFields(mappingsByTab[currentTab], currentTabNewMappings),
		[mappingsByTab, currentTab, currentTabNewMappings]
	);

	const handleAddMapping = () => {
		const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

		setNewMappings((prev) => [
			...prev,
			{
				tempId,
				sourceField: "",
				sourceType: FieldMappingSourceType.FIELD,
				msdynamicsField: "",
				isActive: true,
				entityType,
			},
		]);
	};

	const handleUpdateNewMapping = (tempId: string, data: Partial<NewDynamicsMapping>) => {
		setNewMappings((prev) =>
			prev.map((mapping) => (mapping.tempId === tempId ? { ...mapping, ...data } : mapping))
		);
	};

	const handleConfigureAi = (
		mapping: {
			id?: number;
			tempId?: string;
			msdynamicsField: string;
			entityType: MsdynamicsEntityType | string;
			aiConfig?: FieldMappingAiConfig;
		},
		fields: MsdynamicsField[]
	) => {
		const target = fields.find((field) => field.name === mapping.msdynamicsField) ?? {
			name: mapping.msdynamicsField,
			label: mapping.msdynamicsField,
			type: "String",
		};

		setAiConfigTarget({
			id: mapping.id,
			tempId: mapping.tempId,
			label: target.label,
			targetField: target,
			allFields: fields,
			entityType: mapping.entityType as MsdynamicsEntityType,
			aiConfig: mapping.aiConfig,
		});
	};

	const handleSearchAiProspects = async (query: string) => {
		aiProspectSearchRequestRef.current?.abort?.();
		const request = searchProspects({ page: 1, limit: 10, searchValue: query });

		aiProspectSearchRequestRef.current = request;

		try {
			const result = await request.unwrap();

			return buildAiProspectOptions(result.data);
		} catch {
			return [];
		} finally {
			if (aiProspectSearchRequestRef.current === request) {
				aiProspectSearchRequestRef.current = null;
			}
		}
	};

	const handleSaveAiConfig = async (config: FieldMappingAiConfig) => {
		if (!aiConfigTarget) {
			return;
		}

		if (aiConfigTarget.tempId) {
			handleUpdateNewMapping(aiConfigTarget.tempId, {
				aiConfig: config,
				sourceField: aiConfigTarget.label,
			});
		} else if (aiConfigTarget.id) {
			await updateMapping({
				id: aiConfigTarget.id,
				data: {
					sourceType: FieldMappingSourceType.AI,
					sourceField: aiConfigTarget.label,
					aiConfig: config,
				},
			}).unwrap();
		}

		setAiConfigTarget(null);
	};

	const handleSaveNewMapping = async (mapping: NewDynamicsMapping) => {
		const isIncomplete =
			!mapping.msdynamicsField ||
			(mapping.sourceType !== FieldMappingSourceType.AI && !mapping.sourceField) ||
			(mapping.sourceType === FieldMappingSourceType.AI && !mapping.aiConfig?.prompt);

		if (isIncomplete) {
			return;
		}

		setSavingTempId(mapping.tempId);

		try {
			await createMapping({
				sourceField: mapping.sourceField || mapping.msdynamicsField,
				sourceType: mapping.sourceType,
				msdynamicsField: mapping.msdynamicsField,
				entityType: mapping.entityType as MsdynamicsEntityType,
				isActive: mapping.isActive,
				aiConfig: mapping.aiConfig,
			}).unwrap();
			setNewMappings((prev) => prev.filter((item) => item.tempId !== mapping.tempId));
		} catch (error) {
			console.error("Failed to save Dynamics 365 mapping:", error);
		} finally {
			setSavingTempId(null);
		}
	};

	const tabItems = [
		{ key: DynamicsMappingTab.CONTACTS, label: ts("contacts") },
		{ key: DynamicsMappingTab.ACCOUNTS, label: ts("accounts") },
		{ key: DynamicsMappingTab.LEADS, label: ts("leads") },
		{ key: DynamicsMappingTab.OPPORTUNITIES, label: ts("opportunities") },
		{ key: DynamicsMappingTab.CAMPAIGN_RESPONSES, label: ts("campaignResponses") },
		{ key: DynamicsMappingTab.CAMPAIGNS, label: ts("campaigns") },
		{ key: DynamicsMappingTab.MARKETING_LISTS, label: ts("marketingLists") },
	];

	const renderTabContent = () => {
		const mappings = mappingsByTab[currentTab];
		const targetFields = targetFieldsForTab(currentTab, fieldsByTab[currentTab]);
		const srcFields = sourceFieldsForTab(currentTab, sourceFields);
		const aiCount =
			(mappings?.filter((mapping) => mapping.sourceType === FieldMappingSourceType.AI).length ??
				0) +
			currentTabNewMappings.filter((mapping) => mapping.sourceType === FieldMappingSourceType.AI)
				.length;
		const mappedCount = (mappings?.length ?? 0) + currentTabNewMappings.length;

		return (
			<div className="mt-2">
				<div className="flex items-center gap-3 py-3 border-b border-border-light font-medium text-sm text-text-meta">
					<div className="flex-1">{ts("ourField")}</div>
					<div className="w-[24px]"></div>
					<div className="flex-1">{ts("msdynamicsField")}</div>
					<div className="w-[60px] text-center">{ts("active")}</div>
					<div className="w-[80px]"></div>
				</div>

				{isLoading ? (
					<LoadingBlock className="py-8" />
				) : (
					<>
						{mappings?.map((mapping) => (
							<DynamicsMappingRow
								key={mapping.id}
								mapping={mapping}
								msdynamicsFields={targetFields}
								sourceFields={srcFields}
								ts={ts}
								onConfigureAi={(item) => handleConfigureAi(item, targetFields)}
								onDelete={(id) => deleteMapping(id)}
								onToggleActive={(id, isActive) => updateMapping({ id, data: { isActive } })}
							/>
						))}
						{currentTabNewMappings.map((mapping) => (
							<DynamicsNewMappingRow
								key={mapping.tempId}
								isSaving={savingTempId === mapping.tempId}
								mapping={mapping}
								msdynamicsFields={targetFields}
								sourceFields={srcFields}
								ts={ts}
								usedSourceFields={usedSourceFields}
								usedTargetFieldsByType={usedTargetFieldsByType}
								onCancel={(tempId) =>
									setNewMappings((prev) => prev.filter((item) => item.tempId !== tempId))
								}
								onConfigureAi={(item) => handleConfigureAi(item, targetFields)}
								onSave={handleSaveNewMapping}
								onUpdate={handleUpdateNewMapping}
							/>
						))}
						{!mappings?.length && !currentTabNewMappings.length && (
							<div className="py-6 text-center text-text-meta">{ts("noMappings")}</div>
						)}
					</>
				)}

				<div className="mt-4 flex flex-wrap items-center gap-2">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-[16px] h-[16px] text-text mr-2" icon={faPlus} />}
						size={ButtonSize.S}
						title={ts("addMapping")}
						onClick={handleAddMapping}
					/>
					<DynamicsRestoreDefaultsButton entityType={entityType} ts={ts} />
				</div>

				<FieldMappingFooter
					aiCount={aiCount}
					creditPillLabel={ts("footerCreditPill")}
					mappedCount={mappedCount}
					previewLabel={ts("preview.button")}
					summaryLabel={ts("footerSummary", { mapped: mappedCount, ai: aiCount })}
					testIdPrefix="dynamics"
					onPreview={() => setIsPreviewOpen(true)}
				/>
			</div>
		);
	};

	return (
		<Modal handleClose={onClose} isOpened={isOpen} size="lg" title={ts("title")}>
			<div className="p-4">
				<div className="flex border-b border-border-light mb-2">
					{tabItems.map((tab) => (
						<button
							key={tab.key}
							className={classNames(
								"px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer",
								currentTab === tab.key
									? "border-brand-light text-brand-light"
									: "border-transparent text-text-meta hover:text-text hover:border-border-light"
							)}
							type="button"
							onClick={() => setCurrentTab(tab.key)}
						>
							{tab.label}
						</button>
					))}
				</div>
				{renderTabContent()}
			</div>
			<AiConfigModal
				allFields={aiConfigTarget?.allFields ?? []}
				callbacks={{
					onCancelSearchProspects: () => {
						aiProspectSearchRequestRef.current?.abort?.();
						aiProspectSearchRequestRef.current = null;
					},
					onSearchProspects: handleSearchAiProspects,
					onRunTest: (prospectId, prompt, fallbackValue) =>
						testAiMapping({
							prospectId,
							entityType: aiConfigTarget?.entityType ?? entityType,
							msdynamicsField: aiConfigTarget?.targetField.name,
							prompt,
							// Picklist fallbacks are the numeric option values as strings ("1", not "Qualify").
							allowedValues: aiConfigTarget?.targetField.options?.map((option) => option.value),
							fallbackValue,
						}).unwrap(),
				}}
				initialConfig={aiConfigTarget?.aiConfig}
				isOpen={Boolean(aiConfigTarget)}
				labels={buildAiConfigLabels(ts)}
				subtitle={ts("aiConfigSubtitle", { label: aiConfigTarget?.label ?? "" })}
				targetField={aiConfigTarget?.targetField}
				title={ts("aiConfigTitle")}
				onClose={() => setAiConfigTarget(null)}
				onSave={handleSaveAiConfig}
			/>
			<DynamicsMappingPreviewModal
				entityType={entityType}
				isOpen={isPreviewOpen}
				msdynamicsFields={targetFieldsForTab(currentTab, fieldsByTab[currentTab])}
				sourceFields={sourceFieldsForTab(currentTab, sourceFields)}
				ts={ts}
				onCancelSearchProspects={() => {
					aiProspectSearchRequestRef.current?.abort?.();
					aiProspectSearchRequestRef.current = null;
				}}
				onClose={() => setIsPreviewOpen(false)}
				onSearchProspects={handleSearchAiProspects}
			/>
		</Modal>
	);
};
